import { Box, Paper, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Typography } from '@material-ui/core';
import { Canceler } from 'axios';
import queryString from 'query-string';
import React, { Fragment, useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useSnackbar from '../../../hooks/useSnackbar';
import api from '../../../services/api/base';
import { changeUserBanStatus, getBannedUsers, saveViolationHistoryReason } from '../../../services/api/Moderation';
import {
  IBannedUser,
  IComment,
  ICommentRequestParamDto,
  IViolationHistory,
  ModerationSearchType,
  ModerationType,
  ReviewSortOrder,
} from '../../../services/types';
import TablePaginationActions from '../common/PaginationAction';
import Search from '../common/Search';
import BannedUserTableRow from './BannedUserTableRow';
import reducer, { CommentActionTypes } from './Comment.reducer';
import useStyles from './Comment.styles';
import InternalNote from './CommentInternalNote';
import ReportViewer from './CommentReportViewer';

interface IProps {
  isBannedUserMode: boolean;
  isShownReportedOnly: boolean;
}

const RowsPerPage = 50;

const Comment = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const history = useHistory();
  const location = useLocation();

  const { commentId, userId, storyId, seasonId, displayName } = queryString.parse(location.search);

  const { isBannedUserMode, isShownReportedOnly } = props;

  const initialState: ICommentRequestParamDto = {
    userId: userId ? Number(userId) : undefined,
    storyId: storyId ? Number(storyId) : undefined,
    seasonId: seasonId ? Number(seasonId) : undefined,
    commentId: commentId ? Number(commentId) : undefined,
    displayName: displayName ? String(displayName) : undefined,
    order: ReviewSortOrder.Newest,
  };

  const [isListLoading, setIsListLoading] = useState<boolean>(false);
  const [bannedUsers, setBannedUsers] = useState<IBannedUser[]>([]);
  const [page, setPage] = useState<number>(0);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [showReportViewer, setShowReportViewer] = useState<boolean>(false);
  const [showInternalNote, setShowInternalNote] = useState<boolean>(false);
  const [currentViewComment, setCurrentViewComment] = useState<IComment>();
  const [currentViewViolationHistory, setCurrentViewViolationHistory] = useState<IViolationHistory>();
  const [usersSelectedUserId, setusersSelectedUserId] = useState<Set<number>>(new Set<number>());

  const [state, dispatch] = useReducer(reducer, initialState);

  const snackbar = useSnackbar();
  const confirm = useConfirmDialog();

  const usersCanceler = useRef<Canceler>(() => undefined);

  const handleSearchChange = useCallback(
    (searchType: ModerationSearchType, query: string) => {
      if (searchType === ModerationSearchType.DisplayName) {
        dispatch({ type: CommentActionTypes.SetDisplayName, payload: { displayName: query } });
        history.push(`${location.pathname}?displayName=${query}`);
      }

      if (searchType === ModerationSearchType.UserId) {
        dispatch({ type: CommentActionTypes.SetUserId, payload: { userId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?userId=${query}`);
      }

      if (searchType === ModerationSearchType.StoryId) {
        dispatch({ type: CommentActionTypes.SetStoryId, payload: { storyId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?storyId=${query}`);
      }

      if (searchType === ModerationSearchType.SeasonId) {
        dispatch({ type: CommentActionTypes.SetSeasonId, payload: { seasonId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?seasonId=${query}`);
      }

      if (searchType === ModerationSearchType.CommentId) {
        dispatch({ type: CommentActionTypes.SetCommentId, payload: { commentId: query ? Number(query) : undefined } });
        history.push(`${location.pathname}?commentId=${query}`);
      }

      if (!query) {
        history.push(location.pathname);
      }

      setPage(0);
    },
    [history, location],
  );

  const getSearchTypeAndQuery = (): [ModerationSearchType, any] => {
    if (state.storyId) {
      return [ModerationSearchType.StoryId, state.storyId];
    }

    if (state.seasonId) {
      return [ModerationSearchType.SeasonId, state.seasonId];
    }

    if (state.commentId) {
      return [ModerationSearchType.CommentId, state.commentId];
    }

    if (state.displayName) {
      return [ModerationSearchType.DisplayName, state.displayName];
    }

    return [ModerationSearchType.UserId, state.userId];
  };

  const [currentSearchType, currentSearchQuery] = getSearchTypeAndQuery();

  const fetchBannedUsers = useCallback(
    async (page: number, rowsPerPage: number, param: ICommentRequestParamDto) => {
      const params: any = {
        queryType: undefined,
        q: undefined,
        sortBy: undefined,
      };

      if (param.userId) {
        params.queryType = 'userId';
        params.q = param.userId;
      }

      if (param.storyId) {
        params.queryType = 'storyId';
        params.q = param.storyId;
      }

      if (param.seasonId) {
        params.queryType = 'seasonId';
        params.q = param.seasonId;
      }

      if (param.commentId) {
        params.queryType = 'commentId';
        params.q = param.commentId;
      }

      if (param.displayName) {
        params.queryType = 'displayName';
        params.q = param.displayName;
      }

      if (param.order) {
        params.sortBy = param.order;
      }

      setIsListLoading(true);

      usersCanceler.current();

      try {
        const [fetchPromise, cancel] = getBannedUsers(params, page + 1, rowsPerPage);
        usersCanceler.current = cancel;
        const result = await fetchPromise;

        setBannedUsers(result.data.items);
        setTotalCount(result.data?.totalCount || 0);
      } catch (e) {
        if (api.isCancel(e as Error)) return;
      } finally {
        setIsListLoading(false);
        setusersSelectedUserId(new Set<number>());
      }
    },
    [isShownReportedOnly, isBannedUserMode],
  );

  const handlePageChange = useCallback((e: unknown, page: number) => {
    setPage(page);
  }, []);

  const handleBanStatusChange = useCallback(async (userId: number, status: 'ban' | 'unban') => {
    confirm.open({
      title: `Unban this user?`,
      text: `Are you sure you want to unban this user?`,
      proceedButtonText: 'I am sure.',
      cancelButtonText: 'No',
      onProceed: async () => {
        try {
          const result = await changeUserBanStatus(userId, status);
          if (api.isResponseSuccess(result)) {
            setBannedUsers(ps => ps.filter(bannedUser => bannedUser.id !== userId));
          } else {
            snackbar.error(`The status cannot be saved.`);
          }
        } catch (e) {
          snackbar.error(`The status cannot be saved. ${e.message}`);
        }
      },
    });
  }, []);

  const handleReportClick = useCallback((comment: IComment) => {
    setCurrentViewComment(comment);
    setShowReportViewer(true);
    setShowInternalNote(false);
  }, []);

  const handleInternalNoteClick = useCallback((violationHistory: IViolationHistory) => {
    setCurrentViewViolationHistory(violationHistory);
    setShowInternalNote(true);
    setShowReportViewer(false);
  }, []);

  const handleInternalNoteSave = useCallback((violationHistoryId: number, note: string) => {
    setBannedUsers(ps => {
      const cloned = [...ps];

      const bannedUser = cloned.find(c => c.latestViolationHistory.id === violationHistoryId);
      if (bannedUser) {
        bannedUser.latestViolationHistory.reason = note;
      }
      return cloned;
    });

    setShowInternalNote(false);
  }, []);

  const handleInternalNoteClose = useCallback(() => {
    setShowInternalNote(false);
  }, []);

  const handleReportViewerClose = useCallback(() => {
    setShowReportViewer(false);
  }, []);

  const handleSortOptionChange = useCallback((order: ReviewSortOrder) => {
    dispatch({ type: CommentActionTypes.SetOrder, payload: { order } });
  }, []);

  const ActionsComponent = useCallback(
    TablePaginationActions({
      order: state.order || ReviewSortOrder.Newest,
      disabledButtons: false,
      selectedItemsCount: usersSelectedUserId.size,
      itemsTotalCount: bannedUsers.length,
      disableReportedSortOption: isShownReportedOnly,
      onOrderChange: handleSortOptionChange,
      onApproveButtonClick: undefined,
      onSelectAllCheckboxClick: undefined,
    }),
    [usersSelectedUserId, bannedUsers, handleSortOptionChange],
  );

  useEffect(() => {
    fetchBannedUsers(page, RowsPerPage, state);

    return () => usersCanceler.current();
  }, [fetchBannedUsers, page, state]);

  const renderTablePagination = () => (
    <TablePagination
      ActionsComponent={ActionsComponent}
      count={totalCount}
      page={page}
      rowsPerPage={RowsPerPage}
      rowsPerPageOptions={[]}
      onChangePage={handlePageChange}
      labelDisplayedRows={() => null}
      classes={{
        toolbar: classes.paginationToolbar,
      }}
    />
  );

  return (
    <Box display="flex" flexDirection="column" paddingTop={'6px'}>
      <Box className={classes.bannedUserTitleWrap}>
        <Typography variant="h6">Banned Users</Typography>
        <Box className={classes.bannedUserDescription}>
          Once user is banned, all comments of this user will be hidden immediately.
          <br /> Also, This user can no longer write new comments on the entire story and enter radish live.
        </Box>
      </Box>

      <Search query={currentSearchQuery} type={currentSearchType} moderationType={ModerationType.Comments} onChange={handleSearchChange} />
      <Paper className={classes.tablePaperWrap}>
        {isListLoading && <Box className={classes.loadingWrap} />}
        {renderTablePagination()}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding={'default'} className={classes.tableCell80}>
                {isShownReportedOnly ? 'Reported At' : 'Created At'}
              </TableCell>
              <TableCell padding="checkbox" className={classes.tableCell80}>
                User ID
              </TableCell>
              <TableCell>Story ID / Season ID / Episode # / Parent ID / Comments</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Reported</TableCell>
              <TableCell>Internal Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bannedUsers.length > 0 &&
              bannedUsers.map(bannedUser => (
                <BannedUserTableRow
                  key={bannedUser.id}
                  bannedUser={bannedUser}
                  onBanStatusChange={handleBanStatusChange}
                  onReportClick={handleReportClick}
                  onInternalNoteClick={handleInternalNoteClick}
                />
              ))}
          </TableBody>
        </Table>
        {bannedUsers.length === 0 && (
          <Box className={classes.noResult}>
            <span>No result</span>
          </Box>
        )}
        {renderTablePagination()}
      </Paper>

      {showReportViewer && <ReportViewer comment={currentViewComment} onClose={handleReportViewerClose} />}
      {showInternalNote && (
        <InternalNote
          saveFactory={saveViolationHistoryReason}
          violationHistory={currentViewViolationHistory}
          onSave={handleInternalNoteSave}
          onClose={handleInternalNoteClose}
        />
      )}
    </Box>
  );
};

export default Comment;
